export default () => ({
  loading: false,
  sampleProductId: null,
  samplePrice: 15,
  maxSamplesPerOrder: 3,
  showSuccess: false,
  variantAvailable: true, // Reactive property to track current variant availability

  init() {
    // Find the Alpine component root element (the one with x-data="sampleRequest")
    const rootElement = this.$root || this.$el.closest('[x-data="sampleRequest"]') || this.$el;
    
    // Get settings from theme settings (will be passed via data attributes on root element)
    this.sampleProductId = rootElement.dataset.sampleProductId;
    this.maxSamplesPerOrder = parseInt(rootElement.dataset.maxSamples) || 3;
    
    // Initialize variantAvailable from data attribute
    this.variantAvailable = rootElement.dataset.variantAvailable === 'true';
    
    // Initialize product data in store
    const { product, variant } = this.getProductData();
    if (product && variant) {
      this.$store.main.setProduct(product, variant);
    }
    
    // Hide success message after 5 seconds
    this.$watch('showSuccess', (value) => {
      if (value) {
        setTimeout(() => {
          this.showSuccess = false;
        }, 5000);
      }
    });

    // Listen for variant changes - consolidated approach
    document.addEventListener('variant:changed', (e) => {
      if (e.detail && e.detail.variant) {
        this.handleVariantChange(e.detail.variant);
      }
    });

    // Fallback: Listen for changes on variant selectors if variant:changed isn't fired
    // document.addEventListener('change', (e) => {
    //   if (e.target && (e.target.name === 'id' || e.target.type === 'radio' || e.target.tagName === 'SELECT')) {
    //     const variantSelector = e.target.closest('variant-selects, variant-radios, form[action="/cart/add"]');
    //     if (variantSelector) {
    //       setTimeout(() => {
    //         this.handleVariantChangeFromDOM();
    //       }, 100);
    //     }
    //   }
    // });
  },

  // Computed property to check if current variant is already sampled
  get isCurrentVariantSampled() {
    const product = this.$store.main.currentProduct;
    const variant = this.$store.main.currentVariant;
    
    if (!product || !variant) return false;
    
    return this.$store.main.isProductSampled(product.id, variant.id);
  },

  // Computed property to check if sampling is available for current variant
  get isSamplingAvailable() {
    const rootElement = this.$root || this.$el.closest('[x-data="sampleRequest"]');
    if (!rootElement) return true; // Default to true if we can't determine
    
    const sampleStockOnly = rootElement.dataset.sampleStockOnly === 'true';
    
    // If sample_stock_only setting is disabled, always show sampling
    if (!sampleStockOnly) return true;
    
    // If sample_stock_only is enabled, check current variant availability (reactive)
    return this.variantAvailable;
  },

  // Consolidated method to handle variant changes from variant:changed event
  handleVariantChange(variant) {
    // Update reactive property for Alpine reactivity
    this.variantAvailable = variant.available || false;
    
    // Update DOM attributes
    const rootElement = this.$root || this.$el.closest('[x-data="sampleRequest"]');
    if (rootElement) {
      rootElement.setAttribute('data-variant-id', variant.id);
      rootElement.setAttribute('data-variant-title', variant.title || '');
      rootElement.setAttribute('data-variant-sku', variant.sku || '');
      rootElement.setAttribute('data-variant-available', variant.available || false);
    }
    
    // Update the store
    const productData = this.getProductData();
    if (productData.product) {
      this.$store.main.setProduct(productData.product, {
        id: variant.id,
        title: variant.title || '',
        sku: variant.sku || '',
        available: variant.available || false
      });
    }
  },

  // Fallback method to handle variant changes by reading DOM state
  handleVariantChangeFromDOM() {
    // Get variant data from page's variant system
    const variantSelectors = document.querySelector('variant-selects, variant-radios');
    if (variantSelectors && variantSelectors.currentVariant) {
      // Use the consolidated handler
      this.handleVariantChange(variantSelectors.currentVariant);
    } else {
      // Minimal fallback: just update variant ID
      const buyForm = document.querySelector('form[action="/cart/add"]');
      const variantInput = buyForm?.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        const rootElement = this.$root || this.$el.closest('[x-data="sampleRequest"]');
        if (rootElement) {
          rootElement.setAttribute('data-variant-id', variantInput.value);
        }
      }
    }
  },

  async checkSampleLimit() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Count existing samples in cart
      const existingSamples = cart.items.filter(item => 
        item.properties && item.properties._sample_of_product_id
      );
      
      // Check if this specific product/variant combination already exists
      const { product: currentProduct, variant: currentVariant } = this.getProductData();
      
      if (currentProduct && currentVariant) {
        const existingSample = existingSamples.find(item => 
          item.properties._sample_of_product_id == currentProduct.id &&
          item.properties._sample_of_variant_id == currentVariant.id
        );
        
        if (existingSample) {
          return { canAdd: false, reason: 'duplicate' };
        }
      }
      
      if (existingSamples.length >= this.maxSamplesPerOrder) {
        return { canAdd: false, reason: 'limit' };
      }
      
      return { canAdd: true };
    } catch (error) {
      console.error('Error checking cart:', error);
      return { canAdd: true }; // Allow if we can't check
    }
  },

  getProductData() {
    // Find the Alpine component root element (the one with x-data="sampleRequest")
    const rootElement = this.$root || this.$el.closest('[x-data="sampleRequest"]');
    
    if (!rootElement) {
      console.error('Cannot find Alpine component root element');
      return { product: null, variant: null };
    }

    // Get product data from data attributes on the root element
    const productId = rootElement.dataset.productId;
    const productTitle = rootElement.dataset.productTitle;
    const productHandle = rootElement.dataset.productHandle;
    const variantId = rootElement.dataset.variantId;
    const variantTitle = rootElement.dataset.variantTitle;
    const variantSku = rootElement.dataset.variantSku;

    if (!productId || !variantId) {
      console.error('Product data not found in data attributes. Missing productId or variantId');
      
      // Fallback: try to get variant ID from form input
      const buyForm = document.querySelector('form[action="/cart/add"]');
      const variantInput = buyForm.querySelector('input[name="id"]');
      const fallbackVariantId = variantInput ? variantInput.value : null;
      
      // Fallback: get product title from page
      const fallbackTitle = document.querySelector('h1, .product__title, [class*="product-title"]')?.textContent?.trim();
      
      if (fallbackVariantId && fallbackTitle) {
        const product = {
          id: 'unknown',
          title: fallbackTitle,
          handle: window.location.pathname.split('/').pop().replace('.html', '')
        };
        
        const variant = {
          id: fallbackVariantId,
          title: '',
          sku: ''
        };
        return { product, variant };
      }
      
      return { product: null, variant: null };
    }

    const product = {
      id: productId,
      title: productTitle || 'Product',
      handle: productHandle || window.location.pathname.split('/').pop().replace('.html', '')
    };
    
    // Better variant title logic - only use variant title if it's different from product title
    let finalVariantTitle = variantTitle || '';
    if (finalVariantTitle === productTitle || finalVariantTitle === '') {
      // Try to get variant title from selected options on the page
      const selectedOptions = Array.from(document.querySelectorAll('input[type="radio"]:checked, select')).map(el => {
        if (el.type === 'radio') return el.value;
        if (el.tagName === 'SELECT') return el.options[el.selectedIndex]?.text || el.value;
        return '';
      }).filter(Boolean);
      
      if (selectedOptions.length > 0) {
        finalVariantTitle = selectedOptions.join(' / ');
      }
    }
    
    const variant = {
      id: variantId,
      title: finalVariantTitle,
      sku: variantSku || ''
    };

    return { product, variant };
  },

  async addSample() {
    if (!this.sampleProductId) {
      alert('Sample product not configured. Please contact support.');
      return;
    }

    // Get product data with fallbacks
    const { product: currentProduct, variant: currentVariant } = this.getProductData();
    
    if (!currentProduct || !currentVariant) {
      console.error('Could not get product or variant data');
      alert('Unable to add sample. Please refresh the page and try again.');
      return;
    }

    // Check sample limit and duplicates
    const limitCheck = await this.checkSampleLimit();
    if (!limitCheck.canAdd) {
      if (limitCheck.reason === 'duplicate') {
        alert('You already have a sample of this product variant in your cart.');
      } else if (limitCheck.reason === 'limit') {
        alert(`Maximum ${this.maxSamplesPerOrder} samples per order allowed.`);
      }
      return;
    }

    this.loading = true;
  
    // Better variant title handling
    let displayVariantTitle = currentVariant.title;
    if (!displayVariantTitle || displayVariantTitle === currentProduct.title || displayVariantTitle === 'Default Title') {
      displayVariantTitle = currentProduct.title + ' (Default)';
    }
    
    const formData = {
      items: [{
        id: this.sampleProductId,
        quantity: 1,
        properties: {
          '_sample_of_product_id': currentProduct.id,
          '_sample_of_variant_id': currentVariant.id,
          '_sample_product_title': currentProduct.title,
          '_sample_variant_title': displayVariantTitle,
          '_sample_product_handle': currentProduct.handle
        }
      }]
    };
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        // Success
        this.showSuccess = true;
        
        // Track in store that this product/variant was sampled
        this.$store.main.markProductSampled(currentProduct.id, currentVariant.id);
        
        // Open cart drawer after successful add
        const cartNotification = document.querySelector('cart-notification');
        if (cartNotification) {
          // Fetch updated cart data and render cart contents
          const cartResponse = await fetch('/cart.js');
          const cartData = await cartResponse.json();
          
          // Get cart sections to render
          const sectionsToRender = ['mini-cart-drawer', 'cart-notification-count'];
          const sectionsUrl = window.location.pathname;
          
          // Fetch sections
          const sectionsResponse = await fetch('/cart?sections=' + sectionsToRender.join(',') + '&sections_url=' + encodeURIComponent(sectionsUrl));
          const sectionsData = await sectionsResponse.json();
          
          // Render the cart contents and open
          cartNotification.renderContents({
            item_count: cartData.item_count,
            sections: sectionsData
          });
        } else {
          // Fallback: try to open mini cart directly
          const miniCartDrawer = document.getElementById('mini-cart-drawer');
          if (miniCartDrawer) {
            miniCartDrawer.classList.add('animate', 'active');
            document.querySelector('body').classList.add('added__overlay');
          }
        }
        
        // Dispatch event for other components
        this.$dispatch('cart-updated');
        
        // GA4 tracking if available
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'add_sample_to_cart',
            product_name: currentProduct.title,
            variant_name: currentVariant.title || ''
          });
        }
      } else {
        const errorData = await response.json();
        console.error('Error adding sample:', errorData);
        
        if (errorData.description && errorData.description.includes('sold out')) {
          alert('Sorry, samples are currently sold out. Please try again later.');
        } else {
          alert('Error adding sample to cart. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error adding sample:', error);
      alert('Error adding sample to cart. Please check your connection and try again.');
    } finally {
      this.loading = false;
    }
  }
});